import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@ui/components/Button'
import { GiftIcon } from '@ui/icons/Gift.icon'

import { useGAEvents } from '@utils/callGAEvents'

import { IntroducingBrokerDetailsDto, LoyaltyBonusStatus } from '@model/IntroducingBrokerDetailsDto'
import { LoyaltyDashboardDetailsDto } from '@model/IntroducingBrokerLoyalty'

import { Box } from '@shared/components/Box'
import { ButtonShinyAnimated } from '@shared/components/ButtonShinyAnimated'
import { Loading } from '@shared/components/Loading/Loading'

import { SectionHeader } from '../components/SectionHeader/SectionHeader'
import { MaxLevelBanner } from './MaxLevelBanner'
import { ProgressBars } from './ProgressBars'
import { Tiers } from './Tiers'

import styles from './LoyaltyProgram.module.scss'

interface LoyaltyProgramProps {
  isLoading: boolean
  loyaltyData?: LoyaltyDashboardDetailsDto
  ibData?: IntroducingBrokerDetailsDto
  isConsumeLoyaltyRewardLoading: boolean
  onClaimClick: (reward: { rewardId: string; rewardTierLabel?: string | null }) => void
}

export const LoyaltyProgram: FC<LoyaltyProgramProps> = (props) => {
  const { isLoading, loyaltyData, ibData, isConsumeLoyaltyRewardLoading, onClaimClick } = props

  const { t } = useTranslation()
  const { callGAEvents } = useGAEvents()

  const reward = useMemo<{ rewardId: string; rewardTierLabel?: string | null } | undefined>(() => {
    const reward = ibData?.loyaltyDetails.tiers.find(
      (x) => x.ibReward?.loyaltyBonusStatus.id === LoyaltyBonusStatus.Approved
    )

    if (reward && reward.ibReward?.id) {
      return { rewardId: reward.ibReward.id, rewardTierLabel: reward.label }
    }

    return undefined
  }, [ibData])

  const hasReachedMaxLevel = useMemo<boolean>(() => {
    return (
      !!loyaltyData?.tiers.current &&
      !!loyaltyData.tiers.final &&
      loyaltyData.tiers.current.id === loyaltyData.tiers.final.id
    )
  }, [loyaltyData])

  const handleClaimClick = () => {
    callGAEvents().IBDashboard.LoyaltyProgram.claimRewards()
    if (reward?.rewardId) {
      onClaimClick(reward)
    }
  }

  return (
    <div className={styles.loyaltyProgram}>
      <SectionHeader
        title={t('Loyalty Program.Bonus program')}
        seeMoreLinkProps={{
          onClick: () => callGAEvents().IBDashboard.LoyaltyProgram.seeMore(),
          to: '/dashboard/introducing-broker/bonus-program',
        }}
        tooltipContent={{
          text: t('IB.Dashboard.Eligibility check is being done on weekly basis'),
        }}
      />
      <Loading isLoading={isLoading} showLoadingIcon wrapperClass={styles.loadingWrapper}>
        {loyaltyData?.tiers &&
          (hasReachedMaxLevel ? <MaxLevelBanner /> : <Tiers tiers={loyaltyData.tiers} />)}
        {loyaltyData && <ProgressBars loyaltyData={loyaltyData} ibData={ibData} />}
        <Box flex direction='column' align='center' gap='x16' mt='x32'>
          <Button.Secondary fullWidth size='lg' disabled>
            {t('IB.Dashboard.Checking eligibility')}
          </Button.Secondary>

          <ButtonShinyAnimated fullWidth animated={!!reward?.rewardId}>
            <Button.Primary
              size='lg'
              fullWidth
              iconLeft={GiftIcon}
              disabled={!reward?.rewardId}
              loading={isConsumeLoyaltyRewardLoading}
              onClick={handleClaimClick}
            >
              {t('IB.Dashboard.Claim Rewards')}
            </Button.Primary>
          </ButtonShinyAnimated>
        </Box>
      </Loading>
    </div>
  )
}
